/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import SchoolOptionsController from '../school-options';
import { tracked } from '@glimmer/tracking';
import { set, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import ResponseActivityGroup from '@linc-technologies/linced-common/enums/response-activity-group';
import config from 'linced-app/config/environment';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import {
  responseActivityDetailValidations,
  getResponseActivityDetailErrors,
} from '../../utils/response-activity-validation';
import { filterBy } from '@linc-technologies/linced-common/utils/arrays';
import { attachPendingDrafts } from '@linc-technologies/linced-common/utils/dynamic-fields';
import { serializeFields } from '@linc-technologies/linced-common/utils/dynamic-fields-serializer';

export default class GroupController extends SchoolOptionsController {
  @action
  setDetailValue(value) {
    this.detailValue = value;
  }

  @action
  setReceiptBlurbViolations(value) {
    this.receiptBlurbViolations = value;
  }

  @action
  setSamePeriodsEveryDay(value) {
    this.samePeriodsEveryDay = value;
  }

  @action
  setStatementBlurb2Violations(value) {
    this.statementBlurb2Violations = value;
  }

  @action
  setStatementBlurb3Violations(value) {
    this.statementBlurb3Violations = value;
  }

  @action
  setStatementBlurbViolations(value) {
    this.statementBlurbViolations = value;
  }

  @action
  setStructureEffectiveFrom(value) {
    this.structureEffectiveFrom = value;
  }

  @action
  setStructureName(value) {
    this.structureName = value;
  }

  @tracked
  samePeriodsEveryDay;

  @tracked
  structureEffectiveFrom;

  @tracked
  structureName;

  // Services
  @service
  features;

  @service
  session;

  @service
  store;

  @service
  toaster;

  Jurisdiction = Jurisdiction;
  Scopes = Scopes;
  ResponseActivityGroup = ResponseActivityGroup;
  responseActivityDetailValidations = responseActivityDetailValidations;
  @tracked
  showErrors = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  // Live values for Statement Details blurb fields, used as @text for
  // Finance::ProhibitedLanguageAlert in the template.
  @optionAlias('finance:school:statementBlurb')
  statementBlurb;

  @optionAlias('finance:school:statementBlurb2')
  statementBlurb2;
  @optionAlias('finance:school:statementBlurb3')
  statementBlurb3;
  @optionAlias('finance:school:receiptBlurb')
  receiptBlurb;

  // NSW DoE prohibited financial language (SMOS-012).
  // Populated by Finance::ProhibitedLanguageAlert via @onChange.
  @tracked
  statementBlurbViolations = [];

  @tracked
  statementBlurb2Violations = [];
  @tracked
  statementBlurb3Violations = [];
  @tracked
  receiptBlurbViolations = [];

  // hasStatementDetailsViolations gates the save FAB when any Statement
  // Details blurb field contains prohibited language.
  @computed(
    'statementBlurbViolations.[]',
    'statementBlurb2Violations.[]',
    'statementBlurb3Violations.[]',
    'receiptBlurbViolations.[]',
  )
  get hasStatementDetailsViolations() {
    return (
      !isEmpty(this.statementBlurbViolations) ||
      !isEmpty(this.statementBlurb2Violations) ||
      !isEmpty(this.statementBlurb3Violations) ||
      !isEmpty(this.receiptBlurbViolations)
    );
  }

  @tracked
  detailOptions = null;
  @tracked
  detailOptionDialogOpen = false;
  @tracked detailValue = null;
  @tracked detailKey = null;
  @tracked detailIndex = null;
  @tracked detailIsNew = false;
  @tracked
  detailOptionSaveInProgress = false;
  @tracked deleteDetailOptionDialogOpen = false;
  @tracked detailOptionGroupLabel = null;
  @tracked
  detailOptionDeleteInProgress = false;

  get detailValueIsInvalid() {
    return getResponseActivityDetailErrors(this.detailValue).length > 0;
  }

  // responseActivityDetailRows is a native getter rather than a computed:
  // detailOptions holds plain objects with a nested `details` array, so a
  // two-level @each key is not allowed in Ember. Iterating here tracks both
  // the array and its members.
  get responseActivityDetailRows() {
    if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
      return undefined;
    }
    return this.detailOptions
      .map((option) => {
        let key = option.responseActivityGroup;
        let details = option.details || [];

        return {
          key,
          label: ResponseActivityGroup.getLabel(key) || key,
          details,
        };
      })
      .sort((a, b) => a.key - b.key);
  }

  init() {
    super.init(...arguments);
    this.initRecord();
  }

  saveDetailOptions() {
    let config = this.model.responseActivityConfig;
    if (config.length) {
      config[0].set('configuration', this.detailOptions);
      config = config[0];
    } else {
      config = this.store.createRecord(
        'attendance/response-activity-configuration',
        {
          schoolId: this.session.school,
          configuration: this.detailOptions,
        },
      );
    }
    return config.save();
  }

  resetDetailDialogState() {
    this.detailOptionDialogOpen = false;
    this.detailValue = null;
    this.detailKey = null;
    this.detailIndex = null;
    this.detailIsNew = false;
  }

  resetDetailDeleteState() {
    this.deleteDetailOptionDialogOpen = false;
    this.detailValue = null;
    this.detailOptionGroupLabel = null;
    this.detailKey = null;
    this.detailIndex = null;
    this.detailOptionDeleteInProgress = false;
  }

  @action
  save() {
    let school = this.school;
    // This save does not use school.save(), so add the defaults that the form
    // shows (pending drafts) first. Then they are dirty and saved.
    attachPendingDrafts(this.store, school);

    // An option is a fragment, and JSON.stringify of a fragment fails with a
    // circular structure error. Send the options as plain data.
    let data = {
      options: serializeFields(
        filterBy(this.school.fieldsArray, 'hasDirtyAttributes'),
      ),
    };
    let url = `${config.APP.apiHost}/schools/v4/schools/${school.id}/:setOptions`;
    let chain = this.fetch
      .post(url, { data })
      .then((resp) => {
        // Roll back to clear dirty flags.
        this.school.rollbackAttributes();
        // Push the updated school record into the store.
        this.store.pushPayload('school', resp);
        // Notify about success.
        this.toaster.success('School options saved.');
      })
      .catch(() => {
        this.toaster.error(`Error saving school options.`);
      });

    // Trigger any requested additional action
    let onSave = this.onSave;
    if (!isEmpty(onSave)) {
      chain.then(() => {
        onSave(school);
      });
    }
  }

  @action
  buttonClasses(isInvalid) {
    let classes = ['save', 'md-fab-bottom-right'];
    if (isInvalid) {
      classes.push('invalid');
    }
    return classes.join(' ');
  }

  @action
  handleShowErrors() {
    this.showErrors = true;
  }

  @action
  hideErrors() {
    this.showErrors = false;
  }

  @action
  headingFilter(heading) {
    // We're returning true unless we're looking at
    // certain headings, and certain features are disabled
    if (this.model.group.get('key') === 'nzMOE') {
      if (heading.get('key') === 'automaticUploads') {
        return false;
      }
    }
    if (!this.features.get('heroSuiteEnabled')) {
      // We have a couple of groups which have headings with the same key
      if (heading.get('key') === 'attendance') {
        if (
          this.model.group.get('key') === 'core' ||
          this.model.group.get('key') === 'parents'
        ) {
          return false;
        }
      }
      // We hide a couple of headings under this group
      if (this.model.group.get('key') === 'nzMOE') {
        if (heading.get('key') === 'enrolment') {
          return false;
        }
      }
    }
    if (
      !this.features.get('goalsPowerUpEnabled') &&
      this.model.group.get('key') === 'core' &&
      heading.get('key') === 'studentGoalSetting'
    ) {
      return false;
    }
    return true;
  }

  @action
  openDetailOptionDialog(rowKey, detail, index) {
    this.detailOptionDialogOpen = true;
    this.detailKey = Number(rowKey);
    this.detailValue = detail;
    this.detailIndex = index;
    this.detailIsNew = false;
  }

  @action
  openNewDetailOptionDialog(rowKey) {
    this.detailOptionDialogOpen = true;
    this.detailKey = Number(rowKey);
    this.detailValue = '';
    this.detailIndex = null;
    this.detailIsNew = true;
  }

  @action
  closeDetailOptionDialog() {
    this.resetDetailDialogState();
  }

  @action
  saveDetailOptionChange() {
    let { detailValue, detailKey, detailIndex, detailIsNew } = this;

    if (isEmpty(detailValue)) {
      return;
    }

    let updatedDetailOptions = this.detailOptions.slice() || [];
    let option = updatedDetailOptions.find(
      (item) => item.responseActivityGroup === detailKey,
    );

    if (isEmpty(option)) {
      this.toaster.error('Response activity group not found');
      this.resetDetailDialogState();
      return;
    }

    if (isEmpty(option.details)) {
      set(option, 'details', []);
    }

    if (detailIsNew) {
      option.details.push(detailValue.trim());
    } else {
      option.details[detailIndex] = detailValue.trim();
    }
    this.detailOptions = updatedDetailOptions;

    this.detailOptionSaveInProgress = true;
    this.saveDetailOptions()
      .then(() => {
        this.toaster.success('Successfully Updated Response Activity Detail');
        this.resetDetailDialogState();
      })
      .catch(() => {
        this.toaster.error('Unable to update response activity detail');
      })
      .finally(() => {
        this.detailOptionSaveInProgress = false;
        this.detailOptionDialogOpen = false;
        this.model.responseActivityConfig.update();
      });
  }

  @action
  confirmDeleteDetailOption(rowKey, rowLabel, detail, index) {
    this.deleteDetailOptionDialogOpen = true;
    this.detailKey = Number(rowKey);
    this.detailOptionGroupLabel = rowLabel;
    this.detailValue = detail;
    this.detailIndex = index;
  }

  @action
  cancelDeleteDetailOption() {
    this.resetDetailDeleteState();
  }

  @action
  deleteDetailOption() {
    let { detailKey, detailIndex } = this;

    let updatedDetailOptions = this.detailOptions.slice() || [];
    let option = updatedDetailOptions.find(
      (item) => item.responseActivityGroup === detailKey,
    );

    if (isEmpty(option)) {
      this.toaster.error('Response activity group not found');
      this.resetDetailDeleteState();
      return;
    }

    option.details.splice(detailIndex, 1);
    this.detailOptions = updatedDetailOptions;

    this.detailOptionDeleteInProgress = true;
    this.saveDetailOptions()
      .then(() => {
        this.toaster.success('Successfully Deleted Response Activity Detail');
      })
      .catch(() => {
        this.toaster.error('Unable to delete response activity detail');
      })
      .finally(() => {
        this.detailOptionDeleteInProgress = false;
        this.resetDetailDeleteState();
        this.model.responseActivityConfig.update();
      });
  }
}
